import cn from 'classnames'

import { Translation } from 'cng-common/features/translations/components/translation'
import { ITableColumn } from 'partner360/components/financialTable'
import { TRANSLATION_KEYS } from 'partner360/components/QBR/translations'
import { IQBRPlanAchievementDependOnSettings } from 'partner360/models/qbr/IQBRPlanAchievementDependOnSettings'
import { QBRRounding } from 'partner360/models/qbr/QBRRounding'

import { QbrQuarters } from '../../../../../../models/qbrQuarters'
import styles from '../PlanAchievement.module.scss'

import { getMoneyViewCell } from './getMoneyViewCell'

interface IGetColumnsMoneyViewParameters {
  rounding: QBRRounding
  selectedPeriodFQuarters: number[]
  showUpToDateColumn: boolean
}

export const getColumnsMoneyView = ({
  showUpToDateColumn,
  rounding,
  selectedPeriodFQuarters,
}: IGetColumnsMoneyViewParameters): ITableColumn<IQBRPlanAchievementDependOnSettings>[] => {
  const previousYearColumns: ITableColumn<IQBRPlanAchievementDependOnSettings>[] = [
    {
      key: 'name',
      title: 'Name',
      renderFn: (item) => {
        return (
          <div className={styles.tableCol}>
            <p className={cn(styles.titleText, styles.name)}>{item.Name}</p>
            <div className={cn(styles.financialValue, styles.fullView)}>
              <p className={styles.paragraph}>
                <Translation span text={TRANSLATION_KEYS.financialTable.actual} />
              </p>
              <p className={styles.paragraph}>
                <Translation span text={TRANSLATION_KEYS.financialTable.plan} />
              </p>
              <p className={styles.subParagraph}>
                <Translation span text={TRANSLATION_KEYS.financialTable.actToPlan} />
              </p>
              <p className={styles.subParagraph}>
                <Translation span text={TRANSLATION_KEYS.financialTable.yoy} />
              </p>
            </div>
          </div>
        )
      },
    },
    {
      key: 'Q1',
      title: TRANSLATION_KEYS.financialTable.columns.Q1,
      isTranslatable: true,
      renderFn: (item) => {
        return getMoneyViewCell({
          rounding,
          actuals: item.PrevYearQ1Actuals,
          plans: item.PrevYearQ1Plans,
          actualsToPlans: item.PrevYearQ1ActualsToPlans,
          yoyActuals: item.PrevYearQ1YoYActuals,
        })
      },
    },
    {
      key: 'Q2',
      title: TRANSLATION_KEYS.financialTable.columns.Q2,
      isTranslatable: true,
      renderFn: (item) => {
        return getMoneyViewCell({
          rounding,
          actuals: item.PrevYearQ2Actuals,
          plans: item.PrevYearQ2Plans,
          actualsToPlans: item.PrevYearQ2ActualsToPlans,
          yoyActuals: item.PrevYearQ2YoYActuals,
        })
      },
    },
    {
      key: 'Q3',
      title: TRANSLATION_KEYS.financialTable.columns.Q3,
      isTranslatable: true,
      renderFn: (item) => {
        return getMoneyViewCell({
          rounding,
          actuals: item.PrevYearQ3Actuals,
          plans: item.PrevYearQ3Plans,
          actualsToPlans: item.PrevYearQ3ActualsToPlans,
          yoyActuals: item.PrevYearQ3YoYActuals,
        })
      },
    },
    {
      key: 'Q4',
      title: TRANSLATION_KEYS.financialTable.columns.Q4,
      isTranslatable: true,
      renderFn: (item) => {
        return getMoneyViewCell({
          rounding,
          actuals: item.PrevYearQ4Actuals,
          plans: item.PrevYearQ4Plans,
          actualsToPlans: item.PrevYearQ4ActualsToPlans,
          yoyActuals: item.PrevYearQ4YoYActuals,
        })
      },
    },
  ]

  if (showUpToDateColumn) {
    previousYearColumns.push({
      key: 'Total UTD',
      title: TRANSLATION_KEYS.financialTable.columns.totalUpToDate,
      isTranslatable: true,
      renderFn: (item) => {
        return getMoneyViewCell({
          rounding,
          actuals: item.PrevYearFYActuals,
          plans: item.PrevYearFYPlans,
          actualsToPlans: item.PrevYearFYActualsToPlans,
          yoyActuals: item.PrevYearFYYoYActuals,
          isTotal: true,
        })
      },
    })
  }

  previousYearColumns.push({
    key: 'Total',
    title: TRANSLATION_KEYS.financialTable.columns.total,
    isTranslatable: true,
    renderFn: (item) => {
      return getMoneyViewCell({
        rounding,
        actuals: item.PrevYearTFYActuals,
        plans: item.PrevYearTFYPlans,
        actualsToPlans: item.PrevYearTFYActualsToPlans,
        yoyActuals: item.PrevYearTFYYoYActuals,
        isTotal: true,
        isAlternativeTotal: showUpToDateColumn,
      })
    },
  })

  const currentYearColumns: ITableColumn<IQBRPlanAchievementDependOnSettings>[] = []

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q1)) {
    currentYearColumns.push({
      key: 'Q1',
      title: TRANSLATION_KEYS.financialTable.columns.Q1,
      isTranslatable: true,
      renderFn: (item) => {
        return getMoneyViewCell({
          rounding,
          actuals: item.CurrentYearQ1Actuals,
          plans: item.CurrentYearQ1Plans,
          actualsToPlans: item.CurrentYearQ1ActualsToPlans,
          yoyActuals: item.CurrentYearQ1YoYActuals,
        })
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q2)) {
    currentYearColumns.push({
      key: 'Q2',
      title: TRANSLATION_KEYS.financialTable.columns.Q2,
      isTranslatable: true,
      renderFn: (item) => {
        return getMoneyViewCell({
          rounding,
          actuals: item.CurrentYearQ2Actuals,
          plans: item.CurrentYearQ2Plans,
          actualsToPlans: item.CurrentYearQ2ActualsToPlans,
          yoyActuals: item.CurrentYearQ2YoYActuals,
        })
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q3)) {
    currentYearColumns.push({
      key: 'Q3',
      title: TRANSLATION_KEYS.financialTable.columns.Q3,
      isTranslatable: true,
      renderFn: (item) => {
        return getMoneyViewCell({
          rounding,
          actuals: item.CurrentYearQ3Actuals,
          plans: item.CurrentYearQ3Plans,
          actualsToPlans: item.CurrentYearQ3ActualsToPlans,
          yoyActuals: item.CurrentYearQ3YoYActuals,
        })
      },
    })
  }

  if (selectedPeriodFQuarters.includes(QbrQuarters.Q4)) {
    currentYearColumns.push({
      key: 'Q4',
      title: TRANSLATION_KEYS.financialTable.columns.Q4,
      isTranslatable: true,
      renderFn: (item) => {
        return getMoneyViewCell({
          rounding,
          actuals: item.CurrentYearQ4Actuals,
          plans: item.CurrentYearQ4Plans,
          actualsToPlans: item.CurrentYearQ4ActualsToPlans,
          yoyActuals: item.CurrentYearQ4YoYActuals,
        })
      },
    })
  }

  currentYearColumns.push({
    key: 'Total',
    title: showUpToDateColumn
      ? TRANSLATION_KEYS.financialTable.columns.totalUpToDate
      : TRANSLATION_KEYS.financialTable.columns.total,
    isTranslatable: true,
    renderFn: (item) => {
      return getMoneyViewCell({
        rounding,
        actuals: item.CurrentYearFYActuals,
        plans: item.CurrentYearFYPlans,
        actualsToPlans: item.CurrentYearFYActualsToPlans,
        yoyActuals: item.CurrentYearFYYoYActuals,
        isTotal: true,
      })
    },
  })

  return [...previousYearColumns, ...currentYearColumns]
}
